// Landing Pages JavaScript Functionality
// =====================================

/**
 * Concert Modal Manager - Handles preview and ticket modals
 */
class ConcertModalManager {
  constructor() {
    this.previewModal = null;
    this.ticketModal = null;
    this.previewIframe = null;
    this.concertData = {};
    this.analytics = new LandingPageAnalytics();
    
    this.init();
  }

  init() {
    this.setupElements();
    this.bindEvents();
    this.loadConcertData();
  }

  setupElements() {
    this.previewModal = document.getElementById('previewModal');
    this.ticketModal = document.getElementById('ticketModal');
    this.previewIframe = document.getElementById('previewIframe');
    
    console.log('Modal elements found:', {
      previewModal: !!this.previewModal,
      ticketModal: !!this.ticketModal,
      previewIframe: !!this.previewIframe
    });
  }

  bindEvents() {
    // Close modals when clicking outside
    if (this.previewModal) {
      this.previewModal.addEventListener('click', (e) => {
        if (e.target === this.previewModal) {
          this.closePreview();
        }
      });
    }

    if (this.ticketModal) {
      this.ticketModal.addEventListener('click', (e) => {
        if (e.target === this.ticketModal) {
          this.closeTicketModal();
        }
      });
    }

    // Close modals with Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closePreview();
        this.closeTicketModal();
      }
    });
  }

  loadConcertData() {
    // Concert data will be populated by PHP
    if (window.concertData) {
      this.concertData = window.concertData;
    }
  }

  openPreview(url) {
    if (!this.previewModal || !this.previewIframe) return;

    const loader = document.getElementById('previewLoader');
    
    // Show modal with loading state
    this.previewModal.classList.add('show');
    
    // Show loader, hide iframe
    if (loader) {
      loader.style.display = 'flex';
    }
    this.previewIframe.style.display = 'none';

    // Add UTM parameters to track traffic from hub page
    const urlWithUTM = this.addUTMParameters(url);
    
    // Track the preview open event
    this.analytics.trackPreviewOpen(url);
    
    // Set up fallback timer
    const fallbackTimer = setTimeout(() => {
      // If iframe hasn't loaded after 8 seconds, open in new tab
      this.closePreview();
      window.open(urlWithUTM, '_blank');
    }, 8000);
    
    // Clear fallback timer if iframe loads successfully
    this.previewIframe.onload = () => {
      clearTimeout(fallbackTimer);
      
      // Hide loader, show iframe
      if (loader) {
        loader.style.display = 'none';
      }
      this.previewIframe.style.display = 'block';
    };
    
    // Handle iframe errors (blocked by X-Frame-Options, etc.)
    this.previewIframe.onerror = () => {
      clearTimeout(fallbackTimer);
      this.closePreview();
      window.open(urlWithUTM, '_blank');
    };
    
    // Load the iframe
    this.previewIframe.src = urlWithUTM;
    
    // Prevent background scrolling
    document.body.style.overflow = 'hidden';
  }

  closePreview() {
    if (!this.previewModal || !this.previewIframe) return;

    const loader = document.getElementById('previewLoader');
    
    this.previewModal.classList.remove('show');
    this.previewIframe.src = '';
    
    // Reset loading state
    if (loader) {
      loader.style.display = 'flex';
    }
    this.previewIframe.style.display = 'none';
    
    // Restore background scrolling
    document.body.style.overflow = '';
  }

  openTicketModal(concertId) {
    if (!this.ticketModal) return;

    const title = document.getElementById('ticketModalTitle');
    const body = document.getElementById('ticketModalBody');
    
    const concert = this.concertData[concertId];
    if (!concert) {
      console.error('Concert not found:', concertId);
      return;
    }
    
    title.textContent = concert.title;
    
    // Build performance options
    let html = '';
    if (concert.performances && concert.performances.length > 0) {
      html += '<div class="performance-options">';
      concert.performances.forEach((performance, index) => {
        // Check both 'ticket' and 'tickets' fields based on logs
        const ticketUrl = performance.ticket || performance.tickets;
        if (ticketUrl) {
          const performanceDate = performance.time || performance.date || `Performance ${index + 1}`;
          html += `
            <div class="performance-option">
              <div class="performance-info">
                <strong>${performanceDate}</strong>
              </div>
              <a href="${ticketUrl}" 
                 class="btn btn-primary" 
                 target="_blank" 
                 rel="noopener"
                 onclick="window.concertModals.analytics.trackTicketClick('${concert.title}', '${performanceDate}')">
                Buy Tickets
              </a>
            </div>
          `;
        }
      });
      html += '</div>';
    } else {
      html = '<p>No ticket information available for this concert.</p>';
    }
    
    body.innerHTML = html;
    this.ticketModal.style.display = 'flex';
    
    // Prevent background scrolling
    document.body.style.overflow = 'hidden';
  }

  closeTicketModal() {
    if (!this.ticketModal) return;

    this.ticketModal.style.display = 'none';
    
    // Restore background scrolling
    document.body.style.overflow = '';
  }

  addUTMParameters(url) {
    const urlObj = new URL(url);
    
    // Add UTM parameters for tracking
    urlObj.searchParams.set('utm_source', 'sounds-of-season-hub');
    urlObj.searchParams.set('utm_medium', 'preview-modal');
    urlObj.searchParams.set('utm_campaign', 'holiday-concerts-2024');
    urlObj.searchParams.set('utm_content', 'event-details-preview');
    
    return urlObj.toString();
  }
}

/**
 * Analytics tracking utilities
 */
class LandingPageAnalytics {
  constructor() {
    this.initializeTracking();
  }

  initializeTracking() {
    // Initialize any analytics setup here
    console.log('Analytics initialized for landing page');
    this.setupScrollTracking();
    this.setupButtonTracking();
  }

  setupButtonTracking() {
    // Track button clicks
    document.querySelectorAll('.btn').forEach(button => {
      button.addEventListener('click', (e) => {
        const action = button.textContent.trim();
        const href = button.href || button.getAttribute('data-url');
        this.trackButtonClick(action, href);
      });
    });
  }

  setupScrollTracking() {
    let scrollDepths = [25, 50, 75, 90];
    let tracked = new Set();

    window.addEventListener('scroll', () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );

      scrollDepths.forEach(depth => {
        if (scrollPercent >= depth && !tracked.has(depth)) {
          tracked.add(depth);
          this.trackScrollDepth(depth);
        }
      });
    });
  }

  trackButtonClick(action, url) {
    // Track with Meta Pixel (Facebook Pixel)
    if (typeof fbq !== 'undefined') {
      fbq('trackCustom', 'ButtonClicked', {
        button_text: action,
        destination_url: url,
        source_page: window.location.pathname
      });
    }

    // Track with Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', 'click', {
        event_category: 'engagement',
        event_label: action,
        destination_url: url,
        custom_parameter_1: 'landing_page_button'
      });
    }

    // Console log for debugging
    console.log('Button clicked:', action, url);
  }

  trackScrollDepth(depth) {
    // Track with Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', 'scroll', {
        event_category: 'engagement',
        event_label: `${depth}%`,
        scroll_depth: depth
      });
    }

    // Track with Meta Pixel
    if (typeof fbq !== 'undefined') {
      fbq('trackCustom', 'ScrollDepth', {
        scroll_depth: depth,
        page_url: window.location.href
      });
    }
  }

  trackPreviewOpen(url) {
    // Extract event name from URL for better tracking
    let eventName = 'unknown';
    if (url.includes('elf-in-concert')) {
      eventName = 'elf-film-orchestra';
    } else if (url.includes('handels-messiah')) {
      eventName = 'handels-messiah';
    } else if (url.includes('clayton-holiday-concert')) {
      eventName = 'clayton-holiday-concert';
    }
    
    // Track with Meta Pixel (Facebook Pixel)
    if (typeof fbq !== 'undefined') {
      fbq('trackCustom', 'EventPreviewOpened', {
        event_name: eventName,
        source_page: 'sounds-of-season-hub',
        preview_url: url
      });
    }
    
    // Track with Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', 'event_preview_opened', {
        event_category: 'engagement',
        event_label: eventName,
        source_page: 'sounds_of_season_hub',
        preview_url: url,
        custom_parameter_1: 'modal_preview'
      });
    }
    
    // Track with Google Analytics Universal (legacy)
    if (typeof ga !== 'undefined') {
      ga('send', 'event', 'Preview', 'Open', eventName, {
        'custom_dimension_1': 'sounds_of_season_hub',
        'custom_dimension_2': 'modal_preview'
      });
    }
    
    // Console log for debugging (remove in production)
    console.log('Preview opened:', eventName, url);
  }

  trackTicketClick(concertTitle, performanceDate) {
    // Track with Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', 'ticket_click', {
        event_category: 'conversion',
        event_label: concertTitle,
        performance_date: performanceDate,
        source_page: 'sounds_of_season_hub'
      });
    }
    
    // Track with Meta Pixel
    if (typeof fbq !== 'undefined') {
      fbq('trackCustom', 'TicketClicked', {
        concert_title: concertTitle,
        performance_date: performanceDate,
        source_page: 'sounds-of-season-hub'
      });
    }
    
    console.log('Ticket clicked:', concertTitle, performanceDate);
  }

  trackEmailSignup(email) {
    // Track with Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', 'email_signup', {
        event_category: 'lead_generation',
        signup_location: 'landing_page_footer',
        campaign_name: 'sounds_of_season',
        page_engagement_time: this.getEngagementTime()
      });
    }
    
    // Track with Meta Pixel
    if (typeof fbq !== 'undefined') {
      fbq('trackCustom', 'EmailSignup', {
        signup_location: 'landing-page-footer',
        campaign_name: 'sounds-of-season'
      });
    }
    
    console.log('Email signup tracked:', email);
  }

  getEngagementTime() {
    // Calculate time spent on page
    return Math.floor((Date.now() - window.pageLoadTime) / 1000) + '_seconds';
  }
}

/**
 * Email Signup Form Handler with AJAX Support
 */
class EmailSignupManager {
    constructor() {
        console.log('EmailSignupManager: Constructor called');
        
        this.form = document.getElementById('kso-email-signup-form');
        this.emailInput = document.getElementById('email-input');
        this.submitButton = document.getElementById('signup-submit-btn');
        this.messageContainer = document.getElementById('form-message');
        
        console.log('EmailSignupManager: Form elements found:', {
            form: !!this.form,
            emailInput: !!this.emailInput,
            submitButton: !!this.submitButton,
            messageContainer: !!this.messageContainer
        });
        
        console.log('EmailSignupManager: ksoPublic object:', window.ksoPublic);
        
        this.init();
    }

    init() {
        if (!this.form) return;
        
        this.bindEvents();
        this.setupValidation();
    }

    bindEvents() {
        this.form.addEventListener('submit', (e) => this.handleSubmit(e));
        this.emailInput.addEventListener('blur', () => this.validateEmail());
        this.emailInput.addEventListener('input', () => this.clearValidationState());
    }

    setupValidation() {
        // Real-time email validation
        this.emailInput.addEventListener('input', () => {
            this.clearMessage();
        });
    }

    async handleSubmit(event) {
        console.log('EmailSignupManager: handleSubmit called');
        event.preventDefault();
        
        if (!this.validateEmail()) {
            console.log('EmailSignupManager: Email validation failed');
            this.showMessage('Please enter a valid email address', 'error');
            return;
        }

        console.log('EmailSignupManager: Starting AJAX submission');
        
        // Check if ksoPublic exists
        if (!window.ksoPublic) {
            console.error('EmailSignupManager: ksoPublic not found - AJAX variables not loaded');
            this.showMessage('Configuration error. Please reload the page.', 'error');
            return;
        }

        this.setLoadingState(true);
        
        try {
            const formData = new FormData(this.form);
            formData.append('action', 'kso_email_signup');
            formData.append('nonce', window.ksoPublic.nonce);
            
            // Add additional tracking data
            formData.append('page_url', window.location.href);
            formData.append('referrer', document.referrer);
            formData.append('user_agent', navigator.userAgent);
            formData.append('screen_resolution', `${screen.width}x${screen.height}`);
            formData.append('timestamp', new Date().toISOString());
            
            console.log('EmailSignupManager: Sending AJAX request to:', window.ksoPublic.ajaxUrl);
            console.log('EmailSignupManager: Form data:', Object.fromEntries(formData));
            
            const response = await fetch(window.ksoPublic.ajaxUrl, {
                method: 'POST',
                body: formData
            });
            
            console.log('EmailSignupManager: Response status:', response.status);
            
            const data = await response.json();
            console.log('EmailSignupManager: Response data:', data);
            
            if (data.success) {
                this.handleSuccess(data.data);
            } else {
                throw new Error(data.data || 'Signup failed');
            }
            
        } catch (error) {
            console.error('Signup error:', error);
            this.handleError(error.message);
        } finally {
            this.setLoadingState(false);
        }
    }

    handleSuccess(data) {
        // Clear form
        this.emailInput.value = '';
        this.emailInput.classList.add('success');
        
        // Hide any error messages
        this.clearMessage();
        
        // Track conversion
        this.trackConversion(data);
        
        // Show success modal
        this.showSuccessModal();
        
        // Reset success state after delay
        setTimeout(() => {
            this.emailInput.classList.remove('success');
        }, 3000);
    }

    showSuccessModal() {
        const modal = document.getElementById('signupSuccessModal');
        if (modal) {
            modal.style.display = 'flex';
            document.body.style.overflow = 'hidden';
            
            // Set up modal close handlers
            window.closeSuccessModal = () => {
                modal.style.display = 'none';
                document.body.style.overflow = '';
                
                // Optional: reload page after modal closes
                setTimeout(() => {
                    window.location.reload();
                }, 300);
            };
            
            // Close on overlay click
            const overlay = modal.querySelector('.modal-overlay');
            if (overlay) {
                overlay.addEventListener('click', window.closeSuccessModal);
            }
            
            // Close on escape key
            const escHandler = (e) => {
                if (e.key === 'Escape') {
                    window.closeSuccessModal();
                    document.removeEventListener('keydown', escHandler);
                }
            };
            document.addEventListener('keydown', escHandler);
        }
    }

    handleError(message) {
        let errorMessage = message;
        
        // Customize error messages
        if (message.includes('duplicate') || message.includes('already')) {
            errorMessage = `<span class="error-icon">!</span>You're already on our list! We'll see you at the concerts.`;
        } else if (message.includes('invalid')) {
            errorMessage = `<span class="error-icon">!</span>Please check your email address and try again.`;
        } else if (message.includes('rate limit')) {
            errorMessage = `<span class="error-icon">!</span>Please wait a moment before trying again.`;
        } else {
            errorMessage = `<span class="error-icon">!</span>Something went wrong. Please try again or contact us directly.`;
        }
        
        this.emailInput.classList.add('error');
        this.showMessage(errorMessage, 'error');
        
        // Remove error state after delay
        setTimeout(() => {
            this.emailInput.classList.remove('error');
        }, 3000);
    }

    validateEmail() {
        const email = this.emailInput.value.trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        
        if (!email) {
            this.emailInput.setAttribute('aria-invalid', 'true');
            return false;
        }
        
        if (!emailRegex.test(email)) {
            this.emailInput.setAttribute('aria-invalid', 'true');
            return false;
        }
        
        this.emailInput.setAttribute('aria-invalid', 'false');
        return true;
    }

    clearValidationState() {
        this.emailInput.classList.remove('error', 'success');
        this.emailInput.removeAttribute('aria-invalid');
    }

    setLoadingState(loading) {
        this.submitButton.disabled = loading;
        
        if (loading) {
            this.submitButton.classList.add('loading');
            this.submitButton.setAttribute('aria-busy', 'true');
        } else {
            this.submitButton.classList.remove('loading');
            this.submitButton.setAttribute('aria-busy', 'false');
        }
    }

    showMessage(message, type) {
        this.messageContainer.innerHTML = message;
        this.messageContainer.className = `form-message ${type}`;
        this.messageContainer.style.display = 'block';
        this.messageContainer.setAttribute('role', type === 'error' ? 'alert' : 'status');
        
        // Auto-hide success messages after 5 seconds
        if (type === 'success') {
            setTimeout(() => {
                this.clearMessage();
            }, 5000);
        }
    }

    clearMessage() {
        this.messageContainer.style.display = 'none';
        this.messageContainer.innerHTML = '';
        this.messageContainer.className = 'form-message';
        this.messageContainer.removeAttribute('role');
    }

    trackConversion(data) {
        // Google Analytics 4
        if (typeof gtag !== 'undefined') {
            gtag('event', 'email_signup', {
                campaign: data.campaign || 'sounds-of-the-season',
                email_id: data.id,
                source: data.source || 'direct',
                medium: data.medium || 'website'
            });
        }
        
        // Meta Pixel
        if (typeof fbq !== 'undefined') {
            fbq('track', 'Lead', {
                content_name: 'Holiday Concert Email Signup',
                content_category: 'Email Subscription',
                value: 1,
                currency: 'USD'
            });
        }
        
        // Custom analytics
        if (window.ksoAnalytics) {
            window.ksoAnalytics.trackEmailSignup({
                campaign: data.campaign,
                email_id: data.id,
                timestamp: new Date().toISOString()
            });
        }
    }
}

/**
 * Main Landing Page Controller
 */
class LandingPageController {
  constructor() {
    this.modals = null;
    this.emailManager = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeComponents();
      });
    } else {
      this.initializeComponents();
    }
  }

  initializeComponents() {
    // Track page load time for analytics
    window.pageLoadTime = Date.now();
    
    // Wait a bit for any PHP-generated scripts to load
    setTimeout(() => {
      // Initialize components
      this.modals = new ConcertModalManager();
      this.emailManager = new EmailSignupManager();
      
      // Make components globally accessible
      window.concertModals = this.modals;
      window.emailSignupManager = this.emailManager;
      
      console.log('🎵 KSO Landing Pages initialized - single instance');
      console.log('Concert data available:', window.concertData);
      console.log('Preview modal element:', document.getElementById('previewModal'));
      console.log('Ticket modal element:', document.getElementById('ticketModal'));
    }, 100);
  }
}

// Initialize the landing page when this file is loaded
console.log('🎵 KSO Landing Pages JavaScript Loading...');
new LandingPageController();
console.log('🎵 KSO Landing Pages JavaScript Loaded!');

// Global functions for onclick handlers (backward compatibility)
window.openPreview = function(url) {
  console.log('openPreview called with:', url);
  console.log('concertModals available:', !!window.concertModals);
  if (window.concertModals) {
    window.concertModals.openPreview(url);
  } else {
    console.error('concertModals not available yet');
  }
};

window.closePreview = function() {
  console.log('closePreview called');
  if (window.concertModals) {
    window.concertModals.closePreview();
  } else {
    console.error('concertModals not available yet');
  }
};

window.openTicketModal = function(concertId) {
  console.log('openTicketModal called with:', concertId);
  console.log('concertModals available:', !!window.concertModals);
  if (window.concertModals) {
    window.concertModals.openTicketModal(concertId);
  } else {
    console.error('concertModals not available yet');
  }
};

window.closeTicketModal = function() {
  console.log('closeTicketModal called');
  if (window.concertModals) {
    window.concertModals.closeTicketModal();
  } else {
    console.error('concertModals not available yet');  
  }
};

// Note: Initialization is handled by LandingPageController above
// Removed duplicate initializeLandingPages() function that was causing
// double event listener registration and duplicate form submissions

// Export classes for global access
window.EmailSignupManager = EmailSignupManager;
window.ConcertModalManager = ConcertModalManager;